/**
 * Model-comparison state for CADViewerPage (Issue #489 P0): the comparison
 * target, the single/compare view mode, the diff summary and the in-flight
 * flag, plus the handlers that pick a target, run the compare, and leave
 * comparison mode.
 *
 * Extracted verbatim from CADViewerPage — the handlers are unchanged; the few
 * pieces they touch outside their own state (the page error, the active tab,
 * the chat markers) arrive as setters so the page stays the single owner.
 */
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { compareCADModels } from '../../services/cad.service';
import { CADComparisonSummary, CADMarker, CADModel } from '../../types/cad';
import { resolveApiErrorMessage } from '../../utils/apiErrorMessages';

interface UseCadComparisonOptions {
  selectedModel: CADModel | null;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  setRightTab: React.Dispatch<React.SetStateAction<number>>;
  setChatMarkers: React.Dispatch<React.SetStateAction<CADMarker[]>>;
}

export function useCadComparison({
  selectedModel,
  setError,
  setRightTab,
  setChatMarkers,
}: UseCadComparisonOptions) {
  const { t } = useTranslation();
  const [comparisonModel, setComparisonModel] = useState<CADModel | null>(null);
  const [viewerMode, setViewerMode] = useState<'single' | 'compare'>('single');
  const [comparisonSummary, setComparisonSummary] = useState<CADComparisonSummary | null>(null);
  const [comparing, setComparing] = useState(false);

  // Handle setting comparison target
  const handleSetComparisonTarget = useCallback((model: CADModel) => {
    if (model.status !== 'completed') return;
    setComparisonModel(model);
  }, []);

  // Handle starting comparison
  const handleStartComparison = useCallback(async () => {
    if (!selectedModel || !comparisonModel) return;
    setComparing(true);
    try {
      const result = await compareCADModels(selectedModel.id, comparisonModel.id);
      setComparisonSummary(result);
      setViewerMode('compare');
      setRightTab(2); // Switch to chat tab
    } catch (err) {
      // The shell is localized, so the interpolated half must be too — a raw
      // server sentence here reads as English spliced into Japanese.
      setError(t('cad.comparisonFailed', { message: resolveApiErrorMessage(t, err) }));
    } finally {
      setComparing(false);
    }
  }, [selectedModel, comparisonModel, setError, setRightTab, t]);

  // Handle exiting comparison mode
  const handleExitComparison = useCallback(() => {
    setViewerMode('single');
    setComparisonModel(null);
    setComparisonSummary(null);
    setChatMarkers([]);
  }, [setChatMarkers]);

  return {
    comparisonModel,
    setComparisonModel,
    viewerMode,
    comparisonSummary,
    comparing,
    handleSetComparisonTarget,
    handleStartComparison,
    handleExitComparison,
  };
}
